/**
 * Heat Profile–style column facet filters (Contains dropdown, Filter/Search inputs, checkbox values).
 * Shared by Test Repository (TechnicalPageAgGrid) and DCAL Generator (DcalPage). Styling: HeatProfilePage.css.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Input, Spin } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Filter } from 'lucide-react';
import { Checkbox } from '@crc-evans/ui-components';
import get from 'lodash/get';

export const HEAT_STYLE_FACET_PAGE_CAP = 10000;

export const FILTER_CONDITION_OPTIONS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'notContains', label: 'Does not contain' },
];

export function buildEmptyFacetMaps(filterableColumns, initialCondition = 'contains') {
  const conditions = {};
  const selected = {};
  const valueSearch = {};
  filterableColumns.forEach((k) => {
    conditions[k] = initialCondition;
    selected[k] = null;
    valueSearch[k] = '';
  });
  return { conditions, selected, valueSearch };
}

/** Merge header text + single-value facet checkboxes into API search criteria (multi-select is client-only). */
export function buildCriteriaWithColumnFacets({
  filterableColumns,
  columnFilters,
  columnSelectedValues,
  extraCriteria = {},
}) {
  const criteria = { ...extraCriteria };
  filterableColumns.forEach((col) => {
    const sel = columnSelectedValues[col];
    if (sel != null && sel.size === 1) {
      criteria[col] = Array.from(sel)[0];
    }
  });
  Object.keys(columnFilters || {}).forEach((key) => {
    if (criteria[key] != null && String(criteria[key]).length > 0) return;
    const facetSel = columnSelectedValues[key];
    if (facetSel != null && facetSel.size > 0) return;
    const val = (columnFilters[key] || '').trim();
    if (val) criteria[key] = val;
  });
  return criteria;
}

export const HeatStyleCustomSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : { value: opt.value, label: opt.label ?? opt.value }
  );
  const selectedLabel = normalizedOptions.find((o) => o.value === value)?.label ?? value ?? '';

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className={`heat-profile-custom-select ${className} ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="heat-profile-custom-select-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
          if (e.key === 'Escape') setOpen(false);
        }}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
      >
        <span className="heat-profile-custom-select-value">
          {selectedLabel || <span className="placeholder">{placeholder}</span>}
        </span>
        <DownOutlined className="heat-profile-custom-select-arrow" />
      </div>
      {open && (
        <div className="heat-profile-custom-select-dropdown" role="listbox">
          {normalizedOptions.map((opt) => (
            <div
              key={opt.value}
              className={`heat-profile-custom-select-option ${opt.value === value ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              role="option"
              aria-selected={opt.value === value}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * @param {object} options
 * @param {boolean} options.enabled
 * @param {string[]} options.filterableColumns
 * @param {number|null|undefined} options.listCount
 * @param {any[]} options.projectList
 * @param {object} options.searchCriteria — snapshot for facet fetch when opening a column (ref-synced each render)
 * @param {object} options.columnFilters
 * @param {(key: string, value: string) => void} options.onColumnFilterChange
 * @param {(opts: { pageNumber: number, pageSize: number, searchKey: object }) => Promise<any[]>} options.fetchFacetRows
 */
export function useHeatStyleColumnFacets({
  enabled,
  filterableColumns,
  listCount,
  projectList,
  searchCriteria,
  columnFilters,
  onColumnFilterChange,
  fetchFacetRows,
}) {
  const [columnFilterDropdownOpen, setColumnFilterDropdownOpen] = useState(null);
  const [columnFilterConditions, setColumnFilterConditions] = useState(() =>
    buildEmptyFacetMaps(filterableColumns).conditions
  );
  const [columnSelectedValues, setColumnSelectedValues] = useState(() =>
    buildEmptyFacetMaps(filterableColumns).selected
  );
  const [columnValueSearch, setColumnValueSearch] = useState(() =>
    buildEmptyFacetMaps(filterableColumns).valueSearch
  );
  const [filterFacetRows, setFilterFacetRows] = useState([]);
  const [filterFacetLoading, setFilterFacetLoading] = useState(false);

  const facetFetchContextRef = useRef({
    searchCriteria: {},
    listCount: null,
    projectList: [],
  });
  facetFetchContextRef.current = {
    searchCriteria: searchCriteria && typeof searchCriteria === 'object' ? { ...searchCriteria } : {},
    listCount,
    projectList: Array.isArray(projectList) ? projectList : [],
  };

  useEffect(() => {
    if (!enabled) {
      setFilterFacetRows([]);
      setFilterFacetLoading(false);
      return;
    }
    if (!columnFilterDropdownOpen) {
      setFilterFacetRows([]);
      setFilterFacetLoading(false);
      return;
    }
    if (!filterableColumns.includes(columnFilterDropdownOpen)) {
      setFilterFacetRows([]);
      setFilterFacetLoading(false);
      return;
    }

    const snap = facetFetchContextRef.current;
    const sc = snap.searchCriteria && typeof snap.searchCriteria === 'object' ? snap.searchCriteria : {};
    const pl = Array.isArray(snap.projectList) ? snap.projectList : [];
    const fromListCount = snap.listCount != null && Number(snap.listCount) > 0 ? Number(snap.listCount) : 0;
    const fromRowTotal =
      pl.length > 0 && pl[0] && pl[0].totalCount != null ? Number(pl[0].totalCount) : 0;
    const total = fromListCount || fromRowTotal || Math.max(pl.length, 1);
    const pageSize = Math.min(Math.max(total, 1), HEAT_STYLE_FACET_PAGE_CAP);

    let cancelled = false;
    setFilterFacetLoading(true);

    (async () => {
      try {
        const rows = await fetchFacetRows({
          pageNumber: 1,
          pageSize,
          searchKey: Object.keys(sc).length ? sc : undefined,
        });
        if (!cancelled) {
          setFilterFacetRows(Array.isArray(rows) ? rows : []);
        }
      } catch {
        if (!cancelled) setFilterFacetRows([]);
      } finally {
        if (!cancelled) setFilterFacetLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, columnFilterDropdownOpen, filterableColumns, fetchFacetRows]);

  const sortedListForDistinct = useMemo(
    () => (Array.isArray(projectList) ? projectList : []),
    [projectList]
  );

  /** Only build distinct values for the open column — scanning all columns × all rows freezes large tables (e.g. Spares). */
  const distinctColumnValues = useMemo(() => {
    if (!enabled) return {};
    const open = columnFilterDropdownOpen;
    if (!open || !filterableColumns.includes(open)) {
      return {};
    }
    const useFacets = filterFacetRows.length > 0 && !filterFacetLoading;
    const list = useFacets ? filterFacetRows : sortedListForDistinct;
    if (!Array.isArray(list) || list.length === 0) {
      return { [open]: [] };
    }
    const set = new Set();
    for (let i = 0; i < list.length; i += 1) {
      const row = list[i];
      const v = get(row, open);
      const s = v == null ? '' : String(v).trim();
      set.add(s);
    }
    return {
      [open]: Array.from(set).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true })
      ),
    };
  }, [
    enabled,
    sortedListForDistinct,
    filterFacetRows,
    filterFacetLoading,
    columnFilterDropdownOpen,
    filterableColumns,
  ]);

  const hasMultiFacetSelection = useMemo(
    () =>
      enabled &&
      filterableColumns.some((col) => {
        const s = columnSelectedValues[col];
        return s != null && s.size > 1;
      }),
    [enabled, filterableColumns, columnSelectedValues]
  );

  const applyFacetClientFilter = useCallback(
    (rows) => {
      if (!enabled || !Array.isArray(rows)) return rows || [];
      const hasFacetSelection = filterableColumns.some((col) => {
        const sel = columnSelectedValues[col];
        return sel != null && sel.size > 0;
      });
      if (!hasFacetSelection) return rows;
      let list = [...rows];
      filterableColumns.forEach((col) => {
        const sel = columnSelectedValues[col];
        if (sel != null && sel.size > 0) {
          list = list.filter((row) => {
            const v = get(row, col);
            const s = v == null ? '' : String(v).trim();
            return sel.has(s);
          });
        }
      });
      return list;
    },
    [enabled, filterableColumns, columnSelectedValues]
  );

  const handleColumnFilterConditionChange = useCallback((dataIndex, value) => {
    setColumnFilterConditions((prev) => ({ ...prev, [dataIndex]: value }));
  }, []);

  const handleColumnValueSearchChange = useCallback((dataIndex, value) => {
    setColumnValueSearch((prev) => ({ ...prev, [dataIndex]: value }));
  }, []);

  const toggleColumnSelectedValue = useCallback(
    (dataIndex, value, checked) => {
      setColumnSelectedValues((prev) => {
        const next = { ...prev };
        const current = next[dataIndex];
        const distinct = distinctColumnValues[dataIndex] || [];
        const set = current == null ? new Set(distinct) : new Set(current);
        if (checked) set.add(value);
        else set.delete(value);
        next[dataIndex] = set.size === distinct.length ? null : set;
        return next;
      });
    },
    [distinctColumnValues]
  );

  const handleSelectAllColumnValues = useCallback((dataIndex, checked) => {
    setColumnSelectedValues((prev) => ({ ...prev, [dataIndex]: checked ? null : new Set() }));
  }, []);

  const resetColumnFilterPanel = useCallback(
    (dataIndex) => {
      onColumnFilterChange(dataIndex, '');
      handleColumnValueSearchChange(dataIndex, '');
      handleColumnFilterConditionChange(dataIndex, 'contains');
      setColumnSelectedValues((prev) => ({ ...prev, [dataIndex]: null }));
    },
    [onColumnFilterChange, handleColumnValueSearchChange, handleColumnFilterConditionChange]
  );

  const isTextColumnFilterActive = useCallback(
    (dataIndex) => {
      if ((columnFilters[dataIndex] || '').trim()) return true;
      const sel = columnSelectedValues[dataIndex];
      if (sel == null || sel.size === 0) return false;
      const d = distinctColumnValues[dataIndex];
      if (Array.isArray(d) && d.length > 0) {
        return sel.size < d.length;
      }
      // Dropdown closed: distinct not in memory — treat any facet selection as active
      return true;
    },
    [columnFilters, columnSelectedValues, distinctColumnValues]
  );

  const renderColumnFilterOverlay = useCallback(
    (dataIndex) => {
      const distinct = distinctColumnValues[dataIndex] || [];
      const search = (columnValueSearch[dataIndex] || '').toLowerCase();
      const filteredValues = search
        ? distinct.filter((v) => String(v).toLowerCase().includes(search))
        : distinct;
      const selectedSet = columnSelectedValues[dataIndex];
      const allSelected = selectedSet == null;

      return (
        <div className="heat-profile-column-filter-dropdown" onClick={(e) => e.stopPropagation()}>
          <div className="heat-profile-column-filter-dropdown-body">
            {filterFacetLoading && (
              <div className="heat-profile-column-filter-loading">
                <Spin size="small" />
                <span>Loading values…</span>
              </div>
            )}
            <div className="heat-profile-column-filter-group heat-profile-column-filter-group-condition">
              <div
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <HeatStyleCustomSelect
                  className="heat-profile-column-filter-condition-select"
                  value={columnFilterConditions[dataIndex] || 'contains'}
                  onChange={(v) => handleColumnFilterConditionChange(dataIndex, v)}
                  options={FILTER_CONDITION_OPTIONS}
                  placeholder="Condition"
                />
              </div>
            </div>
            <div className="heat-profile-column-filter-group">
              <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm">
                <Input
                  placeholder="Filter..."
                  size="small"
                  value={columnFilters[dataIndex] || ''}
                  onChange={(e) => onColumnFilterChange(dataIndex, e.target.value)}
                />
              </div>
            </div>
            <div className="heat-profile-column-filter-group">
              <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm">
                <Input
                  placeholder="Search..."
                  size="small"
                  value={columnValueSearch[dataIndex] || ''}
                  onChange={(e) => handleColumnValueSearchChange(dataIndex, e.target.value)}
                />
              </div>
            </div>
            <div className="heat-profile-column-filter-values">
              <Checkbox
                className="heat-profile-column-filter-crc-select-all"
                label="(Select All)"
                checked={allSelected}
                onChange={(checked) => handleSelectAllColumnValues(dataIndex, checked)}
                borderColor="#00C9A7"
                tickColor="#00C9A7"
              />
              <div className="heat-profile-column-filter-value-list">
                {filteredValues.map((val) => {
                  const isChecked = selectedSet == null || selectedSet.has(val);
                  const rowLabel = val === '' ? '(Blank)' : String(val);
                  return (
                    <div key={String(val)} className="heat-profile-column-filter-value-item">
                      <Checkbox
                        className="heat-profile-column-filter-crc-row"
                        label={rowLabel}
                        checked={isChecked}
                        onChange={(checked) => toggleColumnSelectedValue(dataIndex, val, checked)}
                        borderColor="#00C9A7"
                        tickColor="#00C9A7"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="heat-profile-column-filter-footer">
              <Button type="link" size="small" onClick={() => resetColumnFilterPanel(dataIndex)}>
                Reset
              </Button>
            </div>
          </div>
        </div>
      );
    },
    [
      filterFacetLoading,
      columnFilterConditions,
      columnFilters,
      columnValueSearch,
      columnSelectedValues,
      distinctColumnValues,
      handleColumnFilterConditionChange,
      handleColumnValueSearchChange,
      handleSelectAllColumnValues,
      toggleColumnSelectedValue,
      resetColumnFilterPanel,
      onColumnFilterChange,
    ]
  );

  const renderTextColumnFilterTrigger = useCallback(
    (dataIndex) => {
      if (!enabled) return null;
      return (
        <Dropdown
          visible={columnFilterDropdownOpen === dataIndex}
          onVisibleChange={(open) => setColumnFilterDropdownOpen(open ? dataIndex : null)}
          overlay={renderColumnFilterOverlay(dataIndex)}
          trigger={['click']}
          overlayClassName="heat-profile-column-filter-overlay"
          getPopupContainer={() => document.body}
        >
          <span
            role="button"
            tabIndex={0}
            className={`heat-profile-column-filter-icon-wrap ${isTextColumnFilterActive(dataIndex) ? 'has-active' : ''}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            aria-label={`Filter column ${dataIndex}`}
          >
            <Filter size={14} strokeWidth={2} className="heat-profile-column-filter-lucide" aria-hidden />
          </span>
        </Dropdown>
      );
    },
    [
      enabled,
      columnFilterDropdownOpen,
      renderColumnFilterOverlay,
      isTextColumnFilterActive,
    ]
  );

  return {
    columnSelectedValues,
    hasMultiFacetSelection,
    applyFacetClientFilter,
    renderTextColumnFilterTrigger,
    resetColumnFilterPanel,
    isTextColumnFilterActive,
  };
}
